import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../core/store/store';
import Navbar from '../../shared/components/Navbar';
import BackgroundImage from '../../shared/components/BackgroundImage';
import ParticlesBackground from '../../shared/components/ParticlesBackground';
import Card from '../../shared/components/Card';
import usePageTranslation from '../../shared/hooks/usePageTranslation';
import userService, { PanelAccess } from '../../shared/services/userService';

// Actions du bloc Référent : chaque bouton est lié à un type de référent ; l'utilisateur ne peut accéder qu'aux boutons dont il a le référent.
// allowAllReferents: true = accessible à tous les référents.
// allowOnlyCm: true = réservé au référent CM uniquement (roles_id === 1).
const REFERENT_ACTIONS: { referentFunction: string; path: string; allowAllReferents?: boolean; allowOnlyCm?: boolean; labelKey?: string }[] = [
  { referentFunction: 'Référent CM', path: '/panel/cm' },
  { referentFunction: 'Référent Admin', path: '/panel/users/roles' },
  { referentFunction: 'Référent Modo', path: '/panel/system/security-logs' },
  { referentFunction: 'Référent Scénariste', path: '/panel/interventions/create' },
  { referentFunction: 'Référent GR', path: '/panel/interventions/manage' },
  { referentFunction: '', path: '/panel/roles/individual', allowOnlyCm: true, labelKey: 'sections.referent.items.manageIndividualRoles' },
];

const Panel: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { t } = usePageTranslation('panel', 'Panel');
  const [panelAccess, setPanelAccess] = useState<PanelAccess[]>([]);
  const [roleDefaultAccess, setRoleDefaultAccess] = useState<{ section: string; has_access: boolean }[]>([]);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [myReferentFunctions, setMyReferentFunctions] = useState<string[]>([]);
  const [loadingReferents, setLoadingReferents] = useState(false);

  // Vérifier les permissions pour afficher certaines sections
  const isAdmin = currentUser?.roles_id && currentUser.roles_id <= 2; // CM ou Admin

  // Charger les accès panel personnalisés
  useEffect(() => {
    const loadPanelAccess = async () => {
      if (!currentUser?.id) {
        setLoadingAccess(false);
        return;
      }

      try {
        // Charger les accès personnalisés de l'utilisateur et les accès par défaut du rôle
        const [userAccessResponse, roleAccessResponse] = await Promise.all([
          userService.getUserPanelAccess(currentUser.id),
          userService.getRoleDefaultAccess(currentUser.roles_id)
        ]);
        
        if (userAccessResponse.success) {
          setPanelAccess(userAccessResponse.data);
        }
        
        if (roleAccessResponse.success) {
          setRoleDefaultAccess(roleAccessResponse.data);
        }
      } catch (error) {
        // Si la table n'existe pas ou erreur, continuer avec les règles par défaut
        console.warn('Impossible de charger les accès panel:', error);
      } finally {
        setLoadingAccess(false);
      }
    };

    loadPanelAccess();
  }, [currentUser?.id, currentUser?.roles_id]);

  // Charger mes référents pour le bloc Référent (membres équipe : roles_id <= 5)
  useEffect(() => {
    const loadMyReferents = async () => {
      if (!currentUser?.id || !currentUser.roles_id || currentUser.roles_id > 5) {
        return;
      }
      setLoadingReferents(true);
      try {
        const res = await userService.getMyReferents();
        if (res.success && res.data?.length) {
          setMyReferentFunctions(res.data.map((r) => r.function));
        }
      } catch {
        // Ignorer si table absente ou erreur
      } finally {
        setLoadingReferents(false);
      }
    };
    loadMyReferents();
  }, [currentUser?.id, currentUser?.roles_id]);

  const hasReferentAccess = useCallback(
    (referentFunction: string) => myReferentFunctions.includes(referentFunction),
    [myReferentFunctions]
  );

  const showReferentBlock = currentUser?.roles_id != null && currentUser.roles_id <= 5;

  // Fonction pour vérifier si l'utilisateur a accès à une section
  const hasAccessToSection = useCallback((sectionId: string, defaultAccess: boolean): boolean => {
    if (!currentUser) return false;
    
    // 1. Chercher un accès personnalisé pour cette section (priorité la plus haute)
    const customAccess = panelAccess.find(a => a.section === sectionId);
    if (customAccess !== undefined) {
      return customAccess.has_access;
    }
    
    // 2. Chercher un accès par défaut défini pour le rôle
    const roleDefault = roleDefaultAccess.find(a => a.section === sectionId);
    if (roleDefault !== undefined) {
      return roleDefault.has_access;
    }
    
    // 3. Sinon, utiliser l'accès par défaut basé sur le rôle_id (logique historique)
    return defaultAccess;
  }, [currentUser, panelAccess, roleDefaultAccess]);

  // Vérifier les accès pour chaque section
  const hasAdminAccess = hasAccessToSection('admin', !!isAdmin);
  const hasModerationAccess = hasAccessToSection('moderation', currentUser?.roles_id ? currentUser.roles_id <= 3 : false);
  const hasStatsAccess = hasAccessToSection('stats', true); // Tous les utilisateurs par défaut
  const hasInterventionsAccess = hasAccessToSection('interventions', true); // Tous les utilisateurs par défaut
  const hasSystemAccess = hasAccessToSection('system', currentUser?.roles_id === 1); // Uniquement CM (rôle 1)

  if (!currentUser) {
    return null;
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header avec bouton retour */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                {t('title')}
              </h1>
              <p className="text-gray-300">
                {t('subtitle')}
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>{t('backToGame')}</span>
            </button>
          </div>

          {/* Contenu du panel */}
          {loadingAccess ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-white">Chargement des accès...</div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Section Administration */}
              {hasAdminAccess && (
                <Card className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-blue-500/20 w-12 h-12 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">{t('sections.admin.title')}</h3>
              </div>
              <p className="text-gray-400 mb-4">{t('sections.admin.description')}</p>
              <div className="space-y-2">
                <button 
                  onClick={() => navigate('/panel/users')}
                  className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left"
                >
                  {t('sections.admin.items.users')}
                </button>
                <button className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left">
                  {t('sections.admin.items.logs')}
                </button>
                <button className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left">
                  {t('sections.admin.items.settings')}
                </button>
                </div>
              </Card>
              )}

              {/* Section Modération */}
              {hasModerationAccess && (
                <Card className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-yellow-500/20 w-12 h-12 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">{t('sections.moderation.title')}</h3>
              </div>
              <p className="text-gray-400 mb-4">{t('sections.moderation.description')}</p>
              <div className="space-y-2">
                <button className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left">
                  {t('sections.moderation.items.reports')}
                </button>
                <button className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left">
                  {t('sections.moderation.items.tickets')}
                </button>
                <button className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left">
                  {t('sections.moderation.items.chat')}
                </button>
                <button 
                  onClick={() => navigate('/panel/system/security-logs')}
                  className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left"
                >
                  Logs de sécurité
                </button>
                </div>
              </Card>
              )}

              {/* Section Statistiques */}
              {hasStatsAccess && (
                <Card className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-green-500/20 w-12 h-12 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">{t('sections.stats.title')}</h3>
              </div>
              <p className="text-gray-400 mb-4">{t('sections.stats.description')}</p>
              <div className="space-y-2">
                <button className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left">
                  {t('sections.stats.items.players')}
                </button>
                <button className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left">
                  {t('sections.stats.items.activity')}
                </button>
                <button className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left">
                  {t('sections.stats.items.performance')}
                </button>
                </div>
              </Card>
              )}

              {/* Section Interventions */}
              {hasInterventionsAccess && (
                <Card className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-red-500/20 w-12 h-12 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">{t('sections.interventions.title')}</h3>
              </div>
              <p className="text-gray-400 mb-4">{t('sections.interventions.description')}</p>
              <div className="space-y-2">
                <button 
                  onClick={() => navigate('/panel/interventions/create')}
                  className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left"
                >
                  {t('sections.interventions.items.create')}
                </button>
                <button 
                  onClick={() => navigate('/panel/interventions/manage')}
                  className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left"
                >
                  {t('sections.interventions.items.manage')}
                </button>
                <button className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left">
                  {t('sections.interventions.items.history')}
                </button>
                </div>
              </Card>
              )}

              {/* Section Système */}
              {hasSystemAccess && (
                <Card className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-gray-500/20 w-12 h-12 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">{t('sections.system.title')}</h3>
              </div>
              <p className="text-gray-400 mb-4">{t('sections.system.description')}</p>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/panel/system/maintenance')}
                  className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left"
                >
                  {t('sections.system.items.maintenance')}
                </button>
                <button 
                  onClick={() => navigate('/panel/system/backup')}
                  className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left"
                >
                  {t('sections.system.items.backup')}
                </button>
                <button 
                  onClick={() => navigate('/panel/system/configuration')}
                  className="w-full px-4 py-2 bg-gray-700/50 hover:bg-gray-700 text-white rounded-lg transition-colors text-left"
                >
                  {t('sections.system.items.logs')}
                </button>
                </div>
              </Card>
              )}

              {/* Bloc Référent : tous les boutons visibles, accès uniquement pour le référent correspondant */}
              {showReferentBlock && (
                <Card className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="bg-cyan-500/20 w-12 h-12 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white">{t('sections.referent.title')}</h3>
                  </div>
                  <p className="text-gray-400 mb-4">{t('sections.referent.description')}</p>
                  {loadingReferents ? (
                    <div className="text-gray-400 text-sm">Chargement...</div>
                  ) : (
                    <div className="space-y-2">
                      {REFERENT_ACTIONS.map((action, index) => {
                        const canAccess = action.allowOnlyCm
                          ? currentUser?.roles_id === 1
                          : action.allowAllReferents
                            ? true
                            : hasReferentAccess(action.referentFunction);
                        const label = action.labelKey ? t(action.labelKey) : t(`sections.referent.items.${action.referentFunction.replace(/\s+/g, '_')}`);
                        return (
                          <button
                            key={action.referentFunction || action.labelKey || index}
                            onClick={() => canAccess && navigate(action.path)}
                            disabled={!canAccess}
                            title={canAccess ? undefined : t('sections.referent.reservedForReferent')}
                            className={`w-full px-4 py-2 rounded-lg transition-colors text-left ${
                              canAccess
                                ? 'bg-gray-700/50 hover:bg-gray-700 text-white cursor-pointer'
                                : 'bg-gray-800/30 text-gray-500 cursor-not-allowed opacity-75'
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                      {myReferentFunctions.length > 0 && (
                        <button
                          type="button"
                          onClick={() => navigate('/panel/support/team')}
                          className="w-full px-4 py-2 rounded-lg transition-colors text-left flex items-center gap-2 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/35 text-amber-100"
                        >
                          <svg className="w-5 h-5 shrink-0 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                            />
                          </svg>
                          {t('sections.referent.ticket.button')}
                        </button>
                      )}
                    </div>
                  )}
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Panel;

